import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Search, ExpandMore, ExpandLess } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    alignItems: "center"
  },
  filterField: {
    minWidth: 170
  },
  searchField: {
    minWidth: 260,
    flex: 1
  },
  retention: {
    padding: theme.spacing(0, 1, 1),
  },
  outcomeFailure: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  actionCell: {
    fontFamily: "monospace",
    whiteSpace: "nowrap"
  },
  targetCell: {
    fontFamily: "monospace",
    fontSize: 12,
    maxWidth: 420,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  targetExpanded: {
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: 0
  },
  actorEmail: {
    display: "block",
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  missingActor: {
    color: theme.palette.text.secondary,
    fontStyle: "italic"
  }
}));

// Prefixos, não ações exatas: o filtro por "contact." traz create, update,
// delete e import de uma vez, e continua valendo se novas ações surgirem.
const actionGroups = [
  { value: "", label: "Todas as areas" },
  { value: "auth.", label: "Acesso e sessão" },
  { value: "user.", label: "Usuários" },
  { value: "contact.", label: "Contatos" },
  { value: "ticket.", label: "Tickets" },
  { value: "internal-ticket.", label: "Tickets internos" },
  { value: "internal-chat.", label: "Chat interno" },
  { value: "whatsapp.", label: "Conexões" },
  { value: "queue.", label: "Filas" },
  { value: "tag.", label: "Tags" },
  { value: "quickAnswer.", label: "Respostas rapidas" },
  { value: "ticketSubject.", label: "Assuntos" },
  { value: "extension.", label: "Ramais" },
  { value: "company.", label: "Empresa" },
  { value: "settings.", label: "Configurações" },
  { value: "api_tool_action.", label: "API Tools" },
  { value: "integrationToken.", label: "Tokens de integração" },
  { value: "license.", label: "Licenca" },
  { value: "media.", label: "Midia" }
];

const formatDateTime = value => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
};

const Audit = () => {
  const classes = useStyles();

  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [retentionDays, setRetentionDays] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const [searchParam, setSearchParam] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [action, setAction] = useState("");
  const [outcome, setOutcome] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-events", {
        params: {
          pageNumber,
          searchParam: appliedSearch || undefined,
          action: action || undefined,
          outcome: outcome || undefined,
          dateFrom: dateFrom || undefined,
          // O input de data manda 00:00; sem empurrar para o fim do dia, o
          // filtro "até hoje" não traria nada do proprio dia.
          dateTo: dateTo ? `${dateTo}T23:59:59.999` : undefined
        }
      });

      setEvents(prev =>
        pageNumber === 1 ? data.events : [...prev, ...data.events]
      );
      setCount(data.count);
      setHasMore(data.hasMore);
      setRetentionDays(data.retentionDays);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, appliedSearch, action, outcome, dateFrom, dateTo]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Qualquer filtro novo volta para a primeira página: sem isto a lista
  // continuaria paginando a consulta anterior.
  const resetAnd = setter => value => {
    setPageNumber(1);
    setter(value);
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    setPageNumber(1);
    setAppliedSearch(searchParam.trim());
  };

  const retentionLabel =
    retentionDays === null
      ? ""
      : retentionDays > 0
      ? `Eventos são mantidos por ${retentionDays} dias.`
      : "Retenção desativada: eventos são mantidos indefinidamente.";

  return (
    <MainContainer>
      <MainHeader>
        <Title>Auditoria</Title>
        <MainHeaderButtonsWrapper>
          <Typography variant="body2" color="textSecondary">
            {count} evento{count === 1 ? "" : "s"}
          </Typography>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <form className={classes.filters} onSubmit={handleSearchSubmit}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Buscar por nome, e-mail, número ou IP"
            value={searchParam}
            onChange={e => setSearchParam(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton size="small" type="submit">
                  <Search fontSize="small" />
                </IconButton>
              )
            }}
          />
          <TextField
            select
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Area"
            value={action}
            onChange={e => resetAnd(setAction)(e.target.value)}
          >
            {actionGroups.map(group => (
              <MenuItem key={group.value || "all"} value={group.value}>
                {group.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Resultado"
            value={outcome}
            onChange={e => resetAnd(setOutcome)(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="success">Sucesso</MenuItem>
            <MenuItem value="failure">Falha</MenuItem>
          </TextField>
          <TextField
            type="date"
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="De"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={e => resetAnd(setDateFrom)(e.target.value)}
          />
          <TextField
            type="date"
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Ate"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={e => resetAnd(setDateTo)(e.target.value)}
          />
        </form>

        {retentionLabel ? (
          <Typography
            variant="caption"
            color="textSecondary"
            className={classes.retention}
            component="div"
          >
            {retentionLabel}
          </Typography>
        ) : null}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data e hora</TableCell>
              <TableCell>Autor</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Resultado</TableCell>
              <TableCell>Alvo</TableCell>
              <TableCell>IP</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(event => {
              const expanded = expandedId === event.id;
              const targetText = event.target
                ? JSON.stringify(event.target)
                : "";
              const details = {
                ...(event.target ? { alvo: event.target } : {}),
                ...(event.metadata ? { metadados: event.metadata } : {}),
                ...(event.requestPath ? { rota: event.requestPath } : {}),
                ...(event.supportMode ? { modoSuporte: true } : {}),
                ...(event.impersonatedByUserId
                  ? { impersonadoPor: event.impersonatedByUserId }
                  : {})
              };

              return (
                <React.Fragment key={event.id}>
                  <TableRow hover>
                    <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                    <TableCell>
                      {event.actorName ? (
                        <>
                          {event.actorName}
                          <span className={classes.actorEmail}>
                            {event.actorEmail}
                          </span>
                        </>
                      ) : (
                        // Autor excluido continua com o id: e o motivo de a
                        // auditoria não ter chave estrangeira para User.
                        <span className={classes.missingActor}>
                          {event.actorUserId
                            ? `usuário ${event.actorUserId} (removido)`
                            : "sistema"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={classes.actionCell}>
                      {event.action}
                      {event.supportMode ? " (suporte)" : ""}
                    </TableCell>
                    <TableCell
                      className={
                        event.outcome === "failure"
                          ? classes.outcomeFailure
                          : undefined
                      }
                    >
                      {event.outcome === "failure" ? "Falha" : "Sucesso"}
                    </TableCell>
                    <TableCell className={classes.targetCell}>
                      <Tooltip title={targetText}>
                        <span>{targetText}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{event.requestIp}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => setExpandedId(expanded ? null : event.id)}
                      >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {expanded ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <pre className={classes.targetExpanded}>
                          {JSON.stringify(details, null, 2)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              );
            })}
            {loading ? <TableRowSkeleton columns={7} /> : null}
            {!loading && !events.length ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum evento encontrado para os filtros selecionados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        {hasMore ? (
          <Button
            fullWidth
            disabled={loading}
            onClick={() => setPageNumber(prev => prev + 1)}
          >
            Carregar mais
          </Button>
        ) : null}
      </Paper>
    </MainContainer>
  );
};

export default Audit;
